import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CircleDollarSign, FileSearch, Package, RefreshCcw, Search, ShoppingBag, UserPlus, Users } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { addons, packages } from '../../data/webServicesData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtCad = (n) => `CA$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '-');

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={14} style={{ color: '#e2c179' }} />
      <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-semibold">{label}</p>
    </div>
    <p className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>{value}</p>
  </div>
);

const ActivityIcon = ({ type }) => {
  const map = { order: ShoppingBag, signup: UserPlus, lead: FileSearch };
  const Icon = map[type] || ShoppingBag;
  const colors = { order: '#4ade80', signup: '#60A5FA', lead: '#e2c179' };
  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: `${colors[type] || '#e2c179'}1A` }}>
      <Icon size={14} style={{ color: colors[type] || '#e2c179' }} />
    </div>
  );
};

const CustomerDetail = ({ email, onBack, t }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/customer?email=${encodeURIComponent(email)}`, { credentials: 'include' });
        if (res.ok) setData(await res.json());
      } catch {}
      setLoading(false);
    })();
  }, [email]);

  return (
    <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} data-testid="admin-customer-detail">
      <button
        className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-8"
        onClick={onBack}
        data-testid="admin-back-btn"
      >
        <ArrowLeft size={15} />
        {t('adBack')}
      </button>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="relative w-8 h-8">
            <div className="absolute inset-0 rounded-full border-2 border-white/10" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent animate-spin" style={{ borderTopColor: '#e2c179' }} />
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-4 mb-10 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            {data?.user?.picture ? (
              <img src={data.user.picture} alt="" className="w-14 h-14 rounded-full border-2 border-white/15" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-black text-lg font-bold" style={{ background: 'linear-gradient(135deg, #706958, #e2c179)' }}>
                {(data?.user?.name || email)[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold text-white truncate" style={{ fontFamily: "'Syne', sans-serif" }} data-testid="detail-customer-name">
                {data?.user?.name || data?.orders?.[0]?.customer_name || email}
              </p>
              <p className="text-sm text-gray-500 truncate">{email}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="text-[10px] font-bold tracking-wide uppercase px-2.5 py-1 rounded-full" style={data?.user ? { background: 'rgba(96,165,250,0.12)', color: '#93c5fd' } : { background: 'rgba(255,255,255,0.06)', color: '#9ca3af' }}>
                  {data?.user ? t('adRegistered') : t('adGuest')}
                </span>
                {data?.user?.created_at && <span className="text-[11px] text-gray-600">{fmtDate(data.user.created_at)}</span>}
                {data?.orders?.[0] && (
                  <span className="text-[11px] text-gray-500">
                    {data.orders[0].customer_email} {data.orders.find((o) => o.customer_name) ? `· ${data.orders.find((o) => o.customer_name).customer_name}` : ''}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] uppercase tracking-wide text-gray-600 mb-0.5">{t('adColSpent')}</p>
              <p className="text-xl font-bold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
                {fmtCad((data?.orders || []).filter((o) => o.payment_status === 'paid').reduce((s, o) => s + (o.deposit || 0), 0))}
              </p>
            </div>
          </div>

          <h3 className="text-base font-semibold text-white mb-4" style={{ fontFamily: "'Syne', sans-serif" }}>{t('adPurchases')}</h3>
          {(data?.orders || []).length === 0 ? (
            <p className="text-sm text-gray-500 mb-10">{t('adNoOrders')}</p>
          ) : (
            <div className="space-y-3 mb-10">
              {data.orders.map((o, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid={`detail-order-${i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <Package size={16} style={{ color: '#e2c179' }} />
                      <p className="text-sm font-semibold text-white">{o.package || 'Custom'}</p>
                      <span className="text-xs text-gray-600">{fmtDate(o.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {o.subscription_id && (
                        <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: 'rgba(226,193,121,0.12)', color: '#e2c179' }}>
                          <RefreshCcw size={9} className="inline mr-1 -mt-0.5" />
                          {o.cancel_at_period_end ? t('dbCancelScheduled') : (o.subscription_status || 'sub')}
                        </span>
                      )}
                      <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={o.payment_status === 'paid' ? { background: 'rgba(74,222,128,0.12)', color: '#4ade80' } : { background: 'rgba(255,255,255,0.06)', color: '#9ca3af' }}>
                        {o.payment_status}
                      </span>
                    </div>
                  </div>
                  {o.addons.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {o.addons.map((a, j) => <span key={j} className="text-[11px] px-2 py-0.5 rounded-full border border-white/10 text-gray-400">{a}</span>)}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
                    <span>{t('dbDeposit')}: <b className="text-gray-300">{fmtCad(o.deposit)}</b></span>
                    <span>Total: <b className="text-gray-300">{fmtCad(o.one_time_total)}</b></span>
                    {o.monthly_total > 0 && <span>{t('dbMonthlyLabel')}: <b className="text-gray-300">{fmtCad(o.monthly_total)}/mo</b></span>}
                    {o.customer_name && <span>{o.customer_name}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {(data?.audit_leads || []).length > 0 && (
            <>
              <h3 className="text-base font-semibold text-white mb-4" style={{ fontFamily: "'Syne', sans-serif" }}>{t('adLeadHistory')}</h3>
              <div className="space-y-2">
                {data.audit_leads.map((l, i) => (
                  <div key={i} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-5 py-3 text-sm">
                    <span className="text-gray-300 truncate">{l.url}</span>
                    <span className="text-gray-500 shrink-0 ml-4">{l.score}/100 · {fmtDate(l.created_at)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </motion.div>
  );
};

export default function AdminPanel({ user }) {
  const { t, language } = useLanguage();
  const [overview, setOverview] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [search, setSearch] = useState('');
  const [service, setService] = useState('');
  const [minSpent, setMinSpent] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/overview`, { credentials: 'include' });
        if (res.ok) setOverview(await res.json());
      } catch {}
    })();
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoadingCustomers(true);
      try {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (service) params.set('service', service);
        if (minSpent) params.set('min_spent', minSpent);
        const res = await fetch(`${BACKEND_URL}/api/admin/users?${params.toString()}`, { credentials: 'include' });
        if (res.ok) setCustomers((await res.json()).customers || []);
      } catch {}
      setLoadingCustomers(false);
    }, 350);
    return () => clearTimeout(timer);
  }, [search, service, minSpent]);

  const serviceOptions = [
    ...packages.map((p) => ({ id: p.id, label: p.name?.[language] || p.name?.en || p.id })),
    ...addons.map((a) => ({ id: a.id, label: a.name[language] || a.name.en })),
    { id: 'ads-google', label: 'Google Ads Management' },
    { id: 'ads-meta', label: 'Meta Ads Management' },
  ];

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-8 pt-32 md:pt-40 pb-24" data-testid="admin-dashboard">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <p className="accent-tag">{t('adTag')}</p>
        <div className="flex items-center gap-4 mb-12">
          {user.picture && <img src={user.picture} alt="" className="w-12 h-12 rounded-full border-2 border-white/15" referrerPolicy="no-referrer" />}
          <h1 className="text-2xl md:text-4xl font-bold text-white" style={{ fontFamily: "'Syne', sans-serif" }} data-testid="admin-title">
            {t('adTitle')}
          </h1>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-12" data-testid="admin-stats">
        <StatCard icon={CircleDollarSign} label={t('adRevenue')} value={fmtCad(overview?.revenue)} testId="stat-revenue" />
        <StatCard icon={RefreshCcw} label={t('adMrr')} value={`${fmtCad(overview?.mrr)}/mo`} testId="stat-mrr" />
        <StatCard icon={Users} label={t('adUsers')} value={overview?.users_count ?? '-'} testId="stat-users" />
        <StatCard icon={ShoppingBag} label={t('adOrders')} value={overview?.paid_orders ?? '-'} testId="stat-orders" />
        <StatCard icon={RefreshCcw} label={t('adSubs')} value={overview?.active_subscriptions ?? '-'} testId="stat-subs" />
        <StatCard icon={FileSearch} label={t('adLeads')} value={overview?.audit_leads ?? '-'} testId="stat-leads" />
      </div>

      {selected ? (
        <CustomerDetail email={selected} onBack={() => setSelected(null)} t={t} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Customers */}
          <div className="lg:col-span-2">
            <h2 className="text-lg font-semibold text-white mb-5" style={{ fontFamily: "'Syne', sans-serif" }}>{t('adCustomers')}</h2>
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="relative flex-1">
                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('adSearchPh')} className="form-input !pl-10 !py-2.5 text-sm"
                  data-testid="admin-search-input"
                />
              </div>
              <select
                value={service} onChange={(e) => setService(e.target.value)}
                className="form-input !py-2.5 text-sm sm:max-w-[200px]"
                data-testid="admin-service-filter"
              >
                <option value="">{t('adAllServices')}</option>
                {serviceOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              <input
                type="number" min="0" value={minSpent} onChange={(e) => setMinSpent(e.target.value)}
                placeholder={t('adMinSpent')} className="form-input !py-2.5 text-sm sm:max-w-[150px]"
                data-testid="admin-minspent-filter"
              />
            </div>

            {loadingCustomers ? (
              <div className="flex justify-center py-14">
                <div className="relative w-8 h-8">
                  <div className="absolute inset-0 rounded-full border-2 border-white/10" />
                  <div className="absolute inset-0 rounded-full border-2 border-transparent animate-spin" style={{ borderTopColor: '#e2c179' }} />
                </div>
              </div>
            ) : customers.length === 0 ? (
              <p className="text-sm text-gray-500 py-10 text-center" data-testid="admin-no-customers">{t('adNoCustomers')}</p>
            ) : (
              <div className="space-y-2" data-testid="admin-customer-list">
                {customers.map((c, i) => (
                  <button
                    key={c.email}
                    className="w-full text-left rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:bg-white/[0.05] hover:border-white/20 transition-all"
                    onClick={() => setSelected(c.email)}
                    data-testid={`admin-customer-row-${i}`}
                  >
                    <div className="flex items-center gap-3">
                      {c.picture ? (
                        <img src={c.picture} alt="" className="w-9 h-9 rounded-full border border-white/15" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-black text-xs font-bold shrink-0" style={{ background: 'linear-gradient(135deg, #706958, #e2c179)' }}>
                          {(c.name || c.email)[0].toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-white truncate">{c.name || c.email}</p>
                          {c.registered && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#60A5FA' }} title={t('adRegistered')} />}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{c.email}</p>
                        {c.services.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {c.services.slice(0, 2).map((s, j) => (
                              <span key={j} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-gray-500">{s}</span>
                            ))}
                            {c.services.length > 2 && <span className="text-[10px] text-gray-600">+{c.services.length - 2}</span>}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-white">{fmtCad(c.total_spent)}</p>
                        <p className="text-[11px] text-gray-600">{c.orders} {t('adColOrders').toLowerCase()}{c.monthly_total > 0 ? ` · ${fmtCad(c.monthly_total)}/mo` : ''}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Activity feed */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-5" style={{ fontFamily: "'Syne', sans-serif" }}>{t('adActivity')}</h2>
            <div className="space-y-3" data-testid="admin-activity-feed">
              {(overview?.activity || []).map((a, i) => (
                <div key={i} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  <ActivityIcon type={a.type} />
                  <div className="min-w-0">
                    <p className="text-[13px] text-gray-300 leading-snug">{a.label}</p>
                    <p className="text-[11px] text-gray-600 mt-0.5">{fmtDate(a.date)}</p>
                  </div>
                </div>
              ))}
              {(overview?.activity || []).length === 0 && <p className="text-sm text-gray-500">-</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
